import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      // Load from localStorage for guest users
      const guestCart = localStorage.getItem('guestCart');
      if (guestCart) {
        try {
          setCart(JSON.parse(guestCart));
        } catch (e) {
          localStorage.removeItem('guestCart');
        }
      }
    }
  }, [isAuthenticated]);

  // Save guest cart to localStorage whenever it changes
  useEffect(() => {
    if (!isAuthenticated && cart.items.length > 0) {
      localStorage.setItem('guestCart', JSON.stringify(cart));
    }
  }, [cart, isAuthenticated]);

  const fetchCart = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart', error);
    }
  };

  // Fetch product details for guest cart
  const fetchProductDetails = async (productId) => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch product:', error);
      return null;
    }
  };

  const addToCart = async (productId, quantity = 1, variantData = null) => {
    setLoading(true);
    
    try {
      if (isAuthenticated) {
        // Authenticated user - use API
        const payload = { 
          product_id: productId, 
          quantity,
          ...(variantData && {
            variant_id: variantData.variant_id,
            color: variantData.color,
            size: variantData.size
          })
        };
        const response = await axios.post(
          `${API}/cart/add`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCart({ ...cart, items: response.data.items });
      } else {
        // Guest user - use localStorage
        const product = await fetchProductDetails(productId);
        if (!product) {
          toast.error('Product not found');
          return;
        }

        // Get price from variant if applicable
        let itemPrice = product.price;
        if (product.has_variants && variantData?.variant_id) {
          const variant = product.variants?.find(v => v.id === variantData.variant_id);
          if (variant) {
            itemPrice = variant.price;
          }
        }

        // For guest cart, match by productId + color + size
        const existingItem = cart.items.find(item => 
          item.product_id === productId &&
          item.color === (variantData?.color || null) &&
          item.size === (variantData?.size || null)
        );
        let newItems;
        
        if (existingItem) {
          // Update quantity
          newItems = cart.items.map(item =>
            item.product_id === productId &&
            item.color === (variantData?.color || null) &&
            item.size === (variantData?.size || null)
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        } else {
          // Add new item
          newItems = [...cart.items, {
            product_id: productId,
            quantity,
            price: itemPrice,
            name: product.name,
            image_url: product.images?.[0] || product.image_url,
            variant_id: variantData?.variant_id || null,
            color: variantData?.color || null,
            size: variantData?.size || null
          }];
        }
        
        setCart({ items: newItems });
        localStorage.setItem('guestCart', JSON.stringify({ items: newItems }));
      }
      toast.success('Added to cart!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId, variantData = null) => {
    try {
      if (isAuthenticated) {
        const response = await axios.post(
          `${API}/cart/remove/${productId}`,
          { color: variantData?.color, size: variantData?.size },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCart({ ...cart, items: response.data.items });
      } else {
        // Guest user - update localStorage (match by product + variant)
        const newItems = cart.items.filter(item => 
          !(item.product_id === productId &&
            item.color === (variantData?.color || null) &&
            item.size === (variantData?.size || null))
        );
        setCart({ items: newItems });
        localStorage.setItem('guestCart', JSON.stringify({ items: newItems }));
      }
      toast.success('Removed from cart');
    } catch (error) {
      toast.error('Failed to remove from cart');
      console.error(error);
    }
  };

  const clearCart = async () => {
    try {
      if (isAuthenticated) {
        await axios.post(
          `${API}/cart/clear`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      // Clear for both guest and authenticated users
      setCart({ items: [] });
      localStorage.removeItem('guestCart');
    } catch (error) {
      console.error('Failed to clear cart', error);
    }
  };

  const getCartTotal = () => {
    return cart.items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cart.items.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartCount
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
